import { CookieKey } from '@/constants';
import { getRefreshPromise, handleApiError } from '@/utils/auth/authInterceptor';
import Cookies from 'js-cookie';

type Config = {
  method?: string;
  headers?: HeadersInit;
  accessToken?: string;
  skipAuth?: boolean;
  refreshToken?: string;
};

// biome-ignore lint/suspicious/noExplicitAny: HTTP request body values vary widely
type Body = Record<string, any>;

type CallService = (hasAuthFlow?: boolean) => (url: string, config: Config, body?: Body) => Promise<Response>;

const callService: CallService =
  (hasAuthFlow = false) =>
  async (url, { skipAuth = false, refreshToken, accessToken, ...customConfig }, body) => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (hasAuthFlow && !skipAuth) {
      const currentRefreshPromise = getRefreshPromise();
      if (currentRefreshPromise) {
        await currentRefreshPromise;
      }

      const _accessToken = accessToken || Cookies.get(CookieKey.AccessToken);
      if (_accessToken) {
        headers.Authorization = `Bearer ${_accessToken}`;
      }
    }

    const config: RequestInit = {
      ...customConfig,
      headers: {
        ...headers,
        ...customConfig.headers,
      },
    };

    if (body) {
      config.body = JSON.stringify(body);
    }

    const response = await fetch(url, config);

    if (!response.ok) {
      if (response.status === 401 && !skipAuth) {
        const originalRequest = { url, config };
        return handleApiError(response, originalRequest, refreshToken);
      }

      return response;
    }

    return response;
  };

type BaseFetcher = (
  baseUrl: string,
  hasAuthFlow?: boolean,
) => {
  get: (endpoint: string, config?: Config, parameters?: URLSearchParams) => Promise<Response>;
  post: (endpoint: string, body: Body, config?: Config, parameters?: URLSearchParams) => Promise<Response>;
  put: (endpoint: string, body: Body, config?: Config, parameters?: URLSearchParams) => Promise<Response>;
  delete: (endpoint: string, body: Body, config?: Config, parameters?: URLSearchParams) => Promise<Response>;
  patch: (endpoint: string, body?: Body, config?: Config, parameters?: URLSearchParams) => Promise<Response>;
};

export const baseFetcher: BaseFetcher = (baseUrl, hasAuthFlow) => {
  const callServiceFn = callService(hasAuthFlow);

  const url = (endpoint: string, parameters?: URLSearchParams) => {
    const queryParams = parameters ? `?${parameters}` : '';
    return `${baseUrl}/${endpoint}/${queryParams}`;
  };

  return {
    get: async (endpoint, config, parameters) => callServiceFn(url(endpoint, parameters), { method: 'GET', ...config }),
    post: async (endpoint, body, config, parameters) =>
      callServiceFn(url(endpoint, parameters), { method: 'POST', ...config }, body),
    put: async (endpoint, body, config, parameters) =>
      callServiceFn(url(endpoint, parameters), { method: 'PUT', ...config }, body),
    delete: async (endpoint, body, config, parameters) =>
      callServiceFn(url(endpoint, parameters), { method: 'DELETE', ...config }, body),
    patch: async (endpoint, body, config, parameters) =>
      callServiceFn(url(endpoint, parameters), { method: 'PATCH', ...config }, body),
  };
};
